import { logSentryError, SENTRY_CONTEXT } from '@libs/common-next';
import { AddItemRequestBody, CartUI } from '@libs/common-node';

const ONE_TIME_AUTOSHIP = 'one_time';

function areStoresMatching(payloadStoreId: string | undefined, groupStoreId: string | number | null) {
    // Loose Equality because undefined and null should output true
    if (payloadStoreId == groupStoreId) {
        return true;
    }

    // We send ps_{country}_web_inv storeId for One-Time STH
    // but cart returns storeId as null
    return payloadStoreId?.includes('web_inv');
}

const isOneTimeAutoship = (payloadItem: AddItemRequestBody) =>
    payloadItem.autoshipFrequency === ONE_TIME_AUTOSHIP ||
    payloadItem.autoshipFrequencyUnitOfMeasure === ONE_TIME_AUTOSHIP;

export function findProductsAddedToCart({ cart, payload }: { cart: CartUI; payload: AddItemRequestBody[] }) {
    const addedProducts = [];

    for (const payloadItem of payload) {
        const hasPayloadAutoshipData =
            Boolean(payloadItem.autoshipFrequency) &&
            Boolean(payloadItem.autoshipFrequencyUnitOfMeasure) &&
            !isOneTimeAutoship(payloadItem);
        const fulfillmentGroup = cart?.fulfillmentGroups?.find(
            group =>
                group.fulfillmentMethod.toLowerCase() === payloadItem.fulfillmentMethod.toLowerCase() &&
                areStoresMatching(payloadItem.storeId, group.storeId)
        );

        const addedProduct = fulfillmentGroup?.items?.find(item => {
            const isMatchingSku = item.sku === payloadItem.sku;

            if (hasPayloadAutoshipData) {
                const isMatchingFrequency = String(item.autoshipFrequency) === String(payloadItem.autoshipFrequency);
                const isMatchingUnit =
                    String(item.autoshipFrequencyUnitOfMeasure) === String(payloadItem.autoshipFrequencyUnitOfMeasure);

                return isMatchingSku && isMatchingFrequency && isMatchingUnit;
            }

            const isNonAutoshipItem = !item.autoshipFrequency && !item.autoshipFrequencyUnitOfMeasure;

            return isMatchingSku && isNonAutoshipItem;
        });

        if (hasPayloadAutoshipData && !addedProduct) {
            logSentryError(new Error('Alert: mismatching carts payload'), {
                sentryContext: SENTRY_CONTEXT.CART_MISMATCHING_PAYLOAD,
                tags: { component: 'findProductsAddedToCart', severity: 'HIGH' },
                extra: {
                    cartId: cart?.id ?? 'unknown',
                    requestPayload: JSON.stringify(payload),
                    apiResponse: JSON.stringify(cart),
                },
            });
        }

        if (addedProduct) {
            addedProducts.push(addedProduct);
        }
    }

    return addedProducts;
}
